// ============================================================================
// Admin console (spec §12.3) — v1 slice: AI example-sentence generation with an
// approval queue. Generating drafts (Claude, server-side) lands them in
// pending_review; an admin approves/rejects here, and approved sentences then
// appear to learners (fill-blank, sentence-order, study spaces).
// ============================================================================

import { el, mount, toast } from '../dom.js';
import { getState } from '../state/store.js';
import { refreshPendingCount } from '../app.js';
import { ROLE, ACCOUNT_STATUS, MEMBERSHIP } from '@core/models/constants.js';
import * as api from '@core/data/api.js';

// Which admin tab is showing. Module-level so it survives full re-renders
// (render() rebuilds the whole view, e.g. when the pending-count badge updates).
let activeAdminTab = 'content';

export function renderAdmin({ navigate }) {
  const container = el('div.container.stack');

  if (!getState().isAdmin) {
    mount(container, el('div.card.stack', {}, [
      el('h1', { text: 'Admin' }),
      el('div.banner.banner--warn', { text: 'This area is for admins only.' }),
    ]));
    return container;
  }

  const genResults = el('div.stack');
  const queue = el('div.stack');
  const myId = getState().profile?.id || null;

  // --- Generation: search a word, then generate sentences for it -----------
  const search = el('input.input', { type: 'search', placeholder: 'Search a word to generate sentences for…' });
  const hits = el('div.stack');
  let searchTimer = null;
  search.oninput = () => {
    clearTimeout(searchTimer);
    const q = search.value.trim();
    if (!q) return hits.replaceChildren();
    searchTimer = setTimeout(async () => {
      try {
        const rows = (await api.search(q)).filter((r) => r.kind === 'word').slice(0, 8);
        hits.replaceChildren(...(rows.length ? rows.map(wordHit) : [el('p.faint', { text: 'No words match.' })]));
      } catch (e) { toast(e.message || 'Search failed', 'error'); }
    }, 220);
  };

  function wordHit(r) {
    const btn = el('button.btn', {
      text: 'Generate →',
      onclick: async () => {
        btn.disabled = true; btn.textContent = 'Generating…';
        try {
          const res = await api.generateSentences(r.id, 2);
          toast(`Generated ${res.generated} draft${res.generated === 1 ? '' : 's'} (pending review)`, 'success');
          loadQueue();
        } catch (e) {
          if (/not_configured/.test(e.message)) {
            toast('Set ANTHROPIC_API_KEY on the project to enable generation.', 'error');
          } else {
            toast(e.message || 'Generation failed', 'error');
          }
        } finally { btn.disabled = false; btn.textContent = 'Generate →'; }
      },
    });
    return el('div.card', { style: 'padding:var(--space-3) var(--space-4)' }, [
      el('div.row', {}, [
        el('div', {}, [el('strong', { text: `${r.title} ` }), el('span.muted', { text: r.subtitle || '' })]),
        el('div.appbar__spacer'), btn,
      ]),
    ]);
  }

  // --- Approval queue -------------------------------------------------------
  async function loadQueue() {
    try {
      const rows = await api.pendingSentences();
      if (!rows.length) { queue.replaceChildren(el('p.muted', { text: 'No pending sentences. Generate some above.' })); return; }
      queue.replaceChildren(...rows.map(queueRow));
    } catch (e) {
      queue.replaceChildren(el('div.banner.banner--warn', { text: e.message || 'Could not load queue' }));
    }
  }

  function queueRow(s) {
    const act = async (approve) => {
      try {
        await api.reviewSentence(s.id, approve);
        toast(approve ? 'Approved' : 'Rejected', approve ? 'success' : 'info');
        row.remove();
        if (!queue.querySelector('.card')) queue.replaceChildren(el('p.muted', { text: 'Queue empty. 🎉' }));
      } catch (e) { toast(e.message || 'Action failed', 'error'); }
    };
    const row = el('div.card.stack', { style: 'gap:var(--space-2)' }, [
      el('div.faint', { text: `${s.hangul || ''} ${s.hanja_form || ''} · ${s.meaning_en || ''} · ${s.source}` }),
      el('div', { style: 'font-family:var(--font-ko);font-size:1.15rem', text: s.text_ko }),
      s.romanization ? el('div.faint', { text: s.romanization }) : false,
      s.translation_en ? el('div.muted', { text: s.translation_en }) : false,
      el('div.grades', { style: 'grid-template-columns:repeat(2,1fr)' }, [
        el('button.btn.grade.grade--good', { text: 'Approve', onclick: () => act(true) }),
        el('button.btn.grade.grade--again', { text: 'Reject', onclick: () => act(false) }),
      ]),
    ]);
    return row;
  }

  // --- Users: activation + role + membership management (spec §12.3 / §4) ---
  const usersBody = el('div.stack');
  const userSearch = el('input.input', { type: 'search', placeholder: 'Search users by name or email…' });
  let userTimer = null;
  userSearch.oninput = () => { clearTimeout(userTimer); userTimer = setTimeout(loadUsers, 250); };

  async function loadUsers() {
    usersBody.replaceChildren(el('p.muted', { text: 'Loading…' }));
    try {
      const rows = await api.adminListUsers(userSearch.value.trim());
      if (!rows.length) { usersBody.replaceChildren(el('p.muted', { text: 'No users match.' })); return; }
      usersBody.replaceChildren(...rows.map(userRow));
    } catch (e) {
      usersBody.replaceChildren(el('div.banner.banner--warn', { text: e.message || 'Could not load users' }));
    }
  }

  function statusBadge(status) {
    const cls = status === ACCOUNT_STATUS.ACTIVE ? 'grade--good'
      : status === ACCOUNT_STATUS.PENDING_ADMIN ? 'grade--hard' : 'grade--again';
    const label = status === ACCOUNT_STATUS.PENDING_ADMIN ? 'pending' : status;
    return el('span', { class: `btn grade ${cls}`, style: 'pointer-events:none;padding:2px 10px;font-size:0.8rem', text: label });
  }

  function userRow(u) {
    const isSelf = u.id === myId;
    const active = u.status === ACCOUNT_STATUS.ACTIVE;

    const roleSel = el('select.input', { style: 'max-width:8rem' },
      [ROLE.STUDENT, ROLE.TUTOR, ROLE.ADMIN].map((r) =>
        el('option', { value: r, text: r, selected: u.role === r })));
    roleSel.disabled = isSelf;
    roleSel.onchange = async () => {
      const prev = u.role;
      try { await api.adminSetRole(u.id, roleSel.value); u.role = roleSel.value; toast(`Role → ${roleSel.value}`, 'success'); }
      catch (e) { roleSel.value = prev; toast(e.message || 'Could not set role', 'error'); }
    };

    const memSel = el('select.input', { style: 'max-width:9rem' },
      [MEMBERSHIP.FREE, MEMBERSHIP.SUBSCRIPTION, MEMBERSHIP.LIFETIME].map((m) =>
        el('option', { value: m, text: m, selected: u.membership_type === m })));
    memSel.onchange = async () => {
      const prev = u.membership_type;
      try { await api.adminSetMembership(u.id, memSel.value); u.membership_type = memSel.value; toast(`Membership → ${memSel.value}`, 'success'); }
      catch (e) { memSel.value = prev; toast(e.message || 'Could not set membership', 'error'); }
    };

    const actBtn = el('button', {
      class: `btn grade ${active ? 'grade--again' : 'grade--good'}`,
      text: active ? 'Deactivate' : 'Activate',
      onclick: async () => {
        actBtn.disabled = true;
        try {
          await api.adminSetActive(u.id, !active);
          toast(active ? 'Deactivated' : 'Activated', active ? 'info' : 'success');
          loadUsers();
          refreshPendingCount();
        } catch (e) { toast(e.message || 'Action failed', 'error'); actBtn.disabled = false; }
      },
    });

    return el('div.card.stack', { style: 'gap:var(--space-2)' }, [
      el('div.row', {}, [
        el('div.stack', { style: 'gap:2px' }, [
          el('div', {}, [
            el('strong', { text: u.display_name || 'New Learner' }),
            u.is_global_admin ? el('span.faint', { text: '  ★ global admin' }) : false,
            isSelf ? el('span.faint', { text: '  (you)' }) : false,
          ]),
          el('div.faint', { text: u.email || '—' }),
        ]),
        el('div.appbar__spacer'),
        statusBadge(u.status),
      ]),
      el('div.row', { style: 'gap:var(--space-2);flex-wrap:wrap;align-items:center' }, [
        el('label.faint', { text: 'Role' }), roleSel,
        el('label.faint', { text: 'Membership' }), memSel,
        el('div.appbar__spacer'),
        actBtn,
      ]),
    ]);
  }

  // --- Tabs -----------------------------------------------------------------
  const contentPanel = el('div.stack', {}, [
    el('div.card.stack', {}, [
      el('h2', { text: 'Generate example sentences' }),
      search, hits, genResults,
    ]),
    el('div.card.stack', {}, [
      el('div.row', {}, [el('h2', { text: 'Approval queue' }), el('div.appbar__spacer'),
        el('button.btn.btn--ghost', { text: 'Refresh', onclick: loadQueue })]),
      queue,
    ]),
  ]);
  const usersPanel = el('div.stack', {}, [
    el('div.card.stack', {}, [
      el('div.row', {}, [el('h2', { text: 'Users' }), el('div.appbar__spacer'),
        el('button.btn.btn--ghost', { text: 'Refresh', onclick: loadUsers })]),
      el('p.muted', { text: 'New signups start as pending and cannot study until activated. Role “admin” grants admin access to this app only (global admins are managed centrally).' }),
      userSearch, usersBody,
    ]),
  ]);
  const tabContent = el('button', { class: 'btn', text: 'AI content' });
  const tabUsers = el('button', { class: 'btn', text: 'Users' });
  let usersLoaded = false;
  function selectTab(which) {
    activeAdminTab = which;
    const onUsers = which === 'users';
    usersPanel.hidden = !onUsers;
    contentPanel.hidden = onUsers;
    tabUsers.className = onUsers ? 'btn btn--primary' : 'btn';
    tabContent.className = onUsers ? 'btn' : 'btn btn--primary';
    if (onUsers && !usersLoaded) { usersLoaded = true; loadUsers(); }
  }
  tabContent.onclick = () => selectTab('content');
  tabUsers.onclick = () => selectTab('users');

  mount(container,
    el('div.card.stack', {}, [
      el('h1', { text: 'Admin' }),
      el('div.row', { style: 'gap:var(--space-2)' }, [tabContent, tabUsers]),
    ]),
    contentPanel,
    usersPanel,
  );

  selectTab(activeAdminTab); // restore the tab that was open before any re-render
  loadQueue();
  return container;
}
